import { useEffect, useState } from 'react';
import type { DayPlan, TasteSummary } from '../lib/types';
import { api, setToken } from '../lib/api';
import { ls } from '../lib/storage';
import { useFavorites } from '../lib/useFavorites';
import { IconClose, IconPlay, IconPlus, IconHeart } from '../components/icons';

type Section = 'taste' | 'plan' | 'appearance' | 'output';
const SECTIONS: Array<{ id: Section; label: string }> = [
  { id: 'taste', label: 'Taste' },
  { id: 'plan', label: 'Daily Plan' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'output', label: 'Output' },
];

// ---- Taste ------------------------------------------------------------------

// open a playlist/collection to browse + act on each song (play / queue / favorite)
function PlaylistDetail({ name, onClose }: { name: string; onClose: () => void }) {
  const [tracks, setTracks] = useState<Array<{ title: string; artist: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [flash, setFlash] = useState<string | null>(null);
  const fav = useFavorites();
  useEffect(() => {
    setLoading(true);
    api
      .libraryTracks(name)
      .then((d) => setTracks(d.tracks ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [name]);
  const note = (m: string) => {
    setFlash(m);
    setTimeout(() => setFlash(null), 1500);
  };
  const ql = q.trim().toLowerCase();
  const filtered = ql ? tracks.filter((t) => `${t.title} ${t.artist}`.toLowerCase().includes(ql)) : tracks;
  const shown = filtered.slice(0, 300);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div
        className="flex max-h-[82vh] w-[min(560px,94vw)] flex-col rounded-2xl border bd surface p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <div className="font-display min-w-0 flex-1 truncate text-lg text-fg">{name}</div>
          <span className="shrink-0 text-xs text-muted">{tracks.length} 首</span>
          <button
            onClick={onClose}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border bd text-muted hover:text-fg"
          >
            <IconClose className="h-4 w-4" />
          </button>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜索这个歌单…"
          className="mb-2 rounded-lg border bd surface px-3 py-2 text-sm outline-none placeholder-faint focus:bd-strong"
        />
        {flash && <div className="mb-2 text-center text-xs text-air">{flash}</div>}
        <ul className="min-h-0 flex-1 divide-y divide-line overflow-auto rounded-lg border bd">
          {loading && <li className="px-3 py-6 text-center text-sm text-faint">加载中…</li>}
          {!loading &&
            shown.map((t, i) => {
              const liked = fav.isLiked(t.title, t.artist);
              return (
                <li key={`${t.title}|${t.artist}|${i}`} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate">
                    {t.title} <span className="text-muted">— {t.artist}</span>
                  </span>
                  <button
                    onClick={() => fav.toggle(t.title, t.artist)}
                    title={liked ? '取消收藏' : '收藏'}
                    className={`shrink-0 transition ${liked ? 'stamp-in text-seal' : 'text-faint hover:text-seal'}`}
                  >
                    <IconHeart filled={liked} className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      api.queue([{ title: t.title, artist: t.artist }]).catch(() => {});
                      note(`已加入队列：${t.title}`);
                    }}
                    title="加入队列"
                    className="shrink-0 text-faint transition hover:text-fg"
                  >
                    <IconPlus className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      api.play([{ title: t.title, artist: t.artist }]).catch(() => {});
                      note(`播放：${t.title}`);
                    }}
                    title="立即播放"
                    className="shrink-0 text-faint transition hover:text-air"
                  >
                    <IconPlay className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          {!loading && filtered.length > 300 && (
            <li className="px-3 py-3 text-center text-xs text-faint">显示前 300 首，搜索以查看更多</li>
          )}
          {!loading && !filtered.length && (
            <li className="px-3 py-6 text-center text-sm text-faint">没有匹配的歌曲</li>
          )}
        </ul>
      </div>
    </div>
  );
}

function ImportPlaylists() {
  const [open, setOpen] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [lists, setLists] = useState<Array<{ name: string; count: number }>>([]);
  const [profile, setProfile] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const refresh = () => api.library().then((d) => setLists(d.playlists ?? [])).catch(() => {});
  useEffect(() => {
    refresh();
    api.tasteProfile().then((d) => setProfile(d.profile ?? '')).catch(() => {});
  }, []);
  const analyze = async () => {
    if (analyzing) return;
    setAnalyzing(true);
    try {
      const d = await api.analyzeLibrary();
      setProfile(d.profile ?? '');
    } catch {
      /* ignore */
    } finally {
      setAnalyzing(false);
    }
  };
  const go = async () => {
    const u = url.trim();
    if (!u || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.importLibrary(u);
      setMsg(`已导入《${r.name}》 ${r.count} 首 (${r.platform})`);
      setUrl('');
      refresh();
    } catch (e) {
      setMsg(`导入失败：${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className="mb-1 text-xs tracking-[0.2em] text-muted">IMPORT PLAYLISTS</div>
      <p className="mb-2 text-sm text-muted">
        粘贴网易云 / QQ 音乐的歌单分享链接，Claudio 会记进你的口味库并据此推荐。
      </p>
      <div className="flex gap-2">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && go()}
          placeholder="网易云 / QQ音乐 歌单分享链接…"
          className="flex-1 rounded-lg border bd surface px-3 py-2 text-sm outline-none placeholder-faint focus:bd-strong"
        />
        <button
          onClick={go}
          disabled={busy}
          className="accent-grad text-on-accent rounded-lg px-4 py-2 text-sm font-semibold transition hover:brightness-110 disabled:opacity-50"
        >
          {busy ? '导入中…' : '导入'}
        </button>
      </div>
      {msg && <p className="mt-2 text-sm text-dim">{msg}</p>}
      {lists.length > 0 && (
        <ul className="mt-3 divide-y divide-line overflow-hidden rounded-lg border bd">
          {lists.map((p) => (
            <li key={p.name}>
              <button
                onClick={() => setOpen(p.name)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:surface-2"
              >
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                <span className="shrink-0 text-muted">{p.count} 首</span>
                <span className="shrink-0 text-faint">›</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && <PlaylistDetail name={open} onClose={() => setOpen(null)} />}
      {lists.length > 0 && (
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs tracking-[0.2em] text-muted">口味画像</span>
            <button
              onClick={analyze}
              disabled={analyzing}
              className="rounded-md border bd px-2 py-1 text-xs text-dim transition hover:bd-strong hover:text-fg disabled:opacity-50"
            >
              {analyzing ? '分析中…' : profile ? '刷新画像' : '生成画像'}
            </button>
          </div>
          {profile ? (
            <pre className="whitespace-pre-wrap rounded-lg border bd surface p-4 text-sm text-dim">{profile}</pre>
          ) : (
            <p className="text-sm text-faint">Claudio 会从你的歌单提炼口味 + 探索方向，用来推荐新歌（不只是重放歌单）。</p>
          )}
        </div>
      )}
    </div>
  );
}

function TasteSection() {
  const [taste, setTaste] = useState<TasteSummary | null>(null);
  useEffect(() => {
    api.taste().then(setTaste).catch(() => {});
  }, []);
  const block = (label: string, text?: string) =>
    text ? (
      <div>
        <div className="mb-1 text-xs tracking-[0.2em] text-muted">{label.toUpperCase()}</div>
        <pre className="whitespace-pre-wrap rounded-lg border bd surface p-4 text-sm text-dim">{text}</pre>
      </div>
    ) : null;
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold">Taste</h2>
      <p className="text-sm text-muted">
        Just tell Claudio what you love in chat — it remembers and writes it down here.
      </p>
      <ImportPlaylists />
      {taste ? (
        <>
          {block('what I love', taste.taste)}
          {block('rhythm', taste.routines)}
          {block('mood rules', taste.moodRules)}
        </>
      ) : (
        <p className="text-sm text-muted">
          Nothing yet — say hi and tell Claudio your vibe.
        </p>
      )}
    </div>
  );
}

// ---- Daily plan -------------------------------------------------------------

function PlanSection() {
  const [plan, setPlan] = useState<DayPlan | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.planToday().then((d) => setPlan(d.plan)).catch(() => {});
  }, []);
  const regen = async () => {
    setBusy(true);
    try {
      setPlan((await api.generatePlan()).plan);
    } finally {
      setBusy(false);
    }
  };
  const queueBlock = (tracks: Array<{ title: string; artist: string }>) =>
    tracks.length && api.queue(tracks).catch(() => {});
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Today's Plan</h2>
        <button
          onClick={regen}
          disabled={busy}
          className="rounded-md border bd px-3 py-1 text-xs transition hover:bd-strong hover:text-fg disabled:opacity-50"
        >
          {busy ? 'planning…' : plan ? 'regenerate' : 'generate'}
        </button>
      </div>
      {plan ? (
        <div className="space-y-4">
          {plan.note && <p className="text-sm italic text-dim">{plan.note}</p>}
          {plan.blocks.map((b, i) => (
            <div key={i} className="rounded-lg border bd surface p-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-xs tracking-[0.2em] text-air">{b.time}</span>
                  <span className="ml-3 text-sm font-medium">{b.label}</span>
                  {b.mood && <span className="ml-2 text-xs text-muted">· {b.mood}</span>}
                </div>
                {b.tracks.length > 0 && (
                  <button
                    onClick={() => queueBlock(b.tracks)}
                    className="shrink-0 rounded-md border bd px-2 py-1 text-xs transition hover:border-air hover:text-air"
                  >
                    + queue
                  </button>
                )}
              </div>
              <ul className="space-y-1 text-sm">
                {b.tracks.map((t, j) => (
                  <li key={j} className="flex items-center gap-2">
                    <span className="text-faint">·</span>
                    <span className="min-w-0 flex-1 truncate">{t.title}</span>
                    <span className="max-w-[45%] shrink-0 truncate text-muted">{t.artist}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed bd p-6 text-center text-sm text-faint">
          {busy ? 'Claudio is sketching the day…' : 'No plan yet — generate one to see the day’s arc.'}
        </div>
      )}
    </div>
  );
}

// ---- Appearance -------------------------------------------------------------

type Theme = 'dark' | 'light';
function applyTheme(t: Theme) {
  document.documentElement.classList.remove('dark', 'light');
  document.documentElement.classList.add(t);
  ls.set('claudio.theme', t);
}

function AppearanceSection() {
  const [theme, setTheme] = useState<Theme>(() => (ls.get('claudio.theme') as Theme) || 'dark');
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Appearance</h2>
      <div className="inline-flex overflow-hidden rounded-lg border bd">
        {(['dark', 'light'] as Theme[]).map((t) => (
          <button
            key={t}
            onClick={() => {
              setTheme(t);
              applyTheme(t);
            }}
            className={`px-5 py-2 text-xs tracking-[0.2em] transition ${
              theme === t ? 'surface-2 text-fg' : 'text-muted hover:text-fg'
            }`}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---- Output -----------------------------------------------------------------

type Speaker = { udn: string; name: string };
function OutputSection() {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [active, setActive] = useState<Speaker | null>(null);
  const [scanning, setScanning] = useState(false);
  const scan = async (doScan = true) => {
    setScanning(doScan);
    try {
      const r = await api.speakers(doScan);
      setSpeakers(r.speakers);
      setActive(r.active);
    } finally {
      setScanning(false);
    }
  };
  useEffect(() => {
    void scan(false);
  }, []);
  const select = async (udn: string | null) => setActive((await api.selectSpeaker(udn)).active);
  const Row = ({ udn, name }: { udn: string | null; name: string }) => {
    const on = (active?.udn ?? null) === udn;
    return (
      <button
        onClick={() => select(udn)}
        className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left text-sm transition ${
          on ? 'border-air/40 bg-air/10 text-fg' : 'bd hover:bd-strong'
        }`}
      >
        <span>{name}</span>
        {on && <span className="text-xs text-air">● active</span>}
      </button>
    );
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Output</h2>
        <button
          onClick={() => scan(true)}
          disabled={scanning}
          className="rounded-md border bd px-3 py-1 text-xs transition hover:bd-strong hover:text-fg disabled:opacity-50"
        >
          {scanning ? 'scanning…' : 'scan network'}
        </button>
      </div>
      <div className="space-y-2">
        <Row udn={null} name="This device (browser / app)" />
        {speakers.map((s) => (
          <Row key={s.udn} udn={s.udn} name={s.name} />
        ))}
        {speakers.length === 0 && (
          <div className="rounded-lg border border-dashed bd p-4 text-center text-sm text-faint">
            No UPnP/DLNA speakers found yet. Make sure one is on the same network, then scan.
          </div>
        )}
      </div>
      <p className="text-xs text-faint">
        Casting mirrors the now-playing track to a speaker over your LAN. Keep this device muted to avoid
        double audio.
      </p>
    </div>
  );
}

// ---- screen -----------------------------------------------------------------

export function Settings() {
  const [sec, setSec] = useState<Section>('taste');
  const logout = () => {
    setToken('');
    location.reload();
  };
  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col gap-3 px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[calc(max(1.75rem,env(safe-area-inset-top))+2.5rem)] md:flex-row md:gap-6 md:pt-2">
      <nav className="flex shrink-0 gap-1 overflow-x-auto md:w-44 md:flex-col md:overflow-visible">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSec(s.id)}
            className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-sm transition md:block md:w-full md:text-left ${
              sec === s.id ? 'surface-2 text-fg' : 'text-muted hover:text-fg'
            }`}
          >
            {s.label}
          </button>
        ))}
        <button
          onClick={logout}
          className="shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-sm text-seal/70 transition hover:text-seal md:mt-4 md:block md:w-full md:text-left"
        >
          退出登录
        </button>
      </nav>
      <div className="pointer-events-none fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] right-4 z-10 font-display text-[10px] tracking-wider text-faint">
        v{__BUILD_TS__}
      </div>
      <div className="min-h-0 min-w-0 flex-1 overflow-auto overscroll-contain md:pt-2">
        {sec === 'taste' && <TasteSection />}
        {sec === 'plan' && <PlanSection />}
        {sec === 'appearance' && <AppearanceSection />}
        {sec === 'output' && <OutputSection />}
      </div>
    </div>
  );
}
